import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import { ChainSelect } from '@/components/ChainSelect'
import { T } from '@/lib/theme'
import { IS_LIVE } from '@/lib/contracts'
import { fmtUsd } from '@/lib/utils'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'

export function TopBar({ tab, setTab, tabs }) {
  const isMobile = useMediaQuery('(max-width: 760px)')
  const [menuOpen, setMenuOpen] = useState(false)
  const { rate: horizonUsd, found: hasPrice } = useHorizonPriceUsd()

  // Whether the tab rail is wider than the space it has. Measured rather than guessed from a
  // breakpoint: the rail width depends on how many tabs exist and how long their labels are, so a
  // hardcoded threshold would go wrong the next time a tab is added or renamed.
  const navRef = useRef(null)
  const [navOverflows, setNavOverflows] = useState(false)
  useEffect(() => {
    const el = navRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const measure = () => setNavOverflows(el.scrollWidth > el.clientWidth + 1)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [tabs, isMobile])

  // Close the drawer when the viewport expands (e.g. device rotation).
  useEffect(() => {
    if (!isMobile) setMenuOpen(false)
  }, [isMobile])

  // Close the drawer when a tab is picked.
  function pick(id) {
    setTab(id)
    setMenuOpen(false)
  }

  // Exact match for a fixed-page tab, or a sub-route match (e.g. /vaults/0x... still highlights
  // the Vaults tab) - the only tab with children today is Vaults, but this stays generic rather
  // than special-casing that one id.
  function isActive(id) {
    return tab === id || tab.startsWith(id + '/')
  }

  // No bar chrome, deliberately: the brand, price chip, nav rail and wallet sit directly on the
  // page background rather than on a distinct glass strip. Still sticky, so the top row stays
  // pinned and reachable as you scroll - only the visible header bar is gone.
  return (
    <header style={{
      position: 'sticky',
      top: 0,
      zIndex: 10,
    }}>
      {/* Full-bleed, deliberately. This row used to sit in a centred 1280px box, which on a wide
          screen left the brand floating well inside the left edge and the wallet button well
          inside the right - both looking unanchored rather than deliberate. The bar now spans the
          viewport, so the brand sits in the top-left corner and the wallet in the top-right, with
          the existing padding keeping them off the glass edge.

          Note this intentionally stops the bar aligning with page content, which IS still centred
          at its own max width. A full-bleed header over centred content is the common arrangement
          and reads as a frame; matching the content width instead would put the wallet button in
          the middle of the screen on a 2560px display. */}
      {/* Three columns, not a flex row, and the reason is the nav.
          `1fr auto 1fr` gives the middle column its natural width with equal space either side, so
          the tab rail sits on the TRUE centre of the screen no matter how wide the brand block or
          the wallet button happen to be. A flex row centres the nav within the gap BETWEEN them
          instead, which drifts off-centre by however much the two sides differ - measured at ~90px
          on a 1920px screen, which is visible.

          The side columns keep their own flex layout, so nothing about the brand or wallet
          grouping changes. On mobile the nav is absent and an empty middle column holds the
          shape. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        gap: isMobile ? 8 : 20,
        padding: isMobile ? '10px 14px' : '12px 24px',
      }}>
        {/* Left column: brand + price chip, hard against the left edge.
            NO `minWidth: 0` here, deliberately. A grid `1fr` track is `minmax(auto, 1fr)`, so it
            normally refuses to shrink below its content - but `min-width: 0` on the item makes its
            min-content contribution zero, letting the track collapse while the brand (which is
            `white-space: nowrap`) keeps its width and paints straight over the nav. That is the
            overlap: fine at 1400px+, broken by about 1200px, which is an ordinary half-screen
            window rather than an edge case.

            Consequence worth stating: once the brand needs more than its 1fr share, the side tracks
            stop being equal and the nav drifts off exact centre. Slightly off-centre beats
            overlapping, and the nav is still centred at every width where all three fit. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 20 }}>
        {/* Logo - links home */}
        <Link to="/" onClick={() => setMenuOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, textDecoration: 'none' }}>
          <img
            src="/horizon.png"
            alt="Horizon"
            width={32}
            height={32}
            style={{
              width: 32, height: 32,
              borderRadius: '50%',
              flexShrink: 0,
            }}
          />
          <div style={{
            fontFamily: T.fontD,
            fontSize: isMobile ? 18 : 22,
            color: T.text,
            fontWeight: 700,
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
          }}>
            {isMobile ? (
              'Horizon'
            ) : (
              <>Horizon<span style={{ color: T.accent }}>.win</span></>
            )}
          </div>
          {!IS_LIVE && !isMobile && (
            <span style={{
              marginLeft: 8,
              padding: '3px 8px',
              borderRadius: 4,
              border: `1px solid ${T.accent}`,
              color: T.accent,
              fontFamily: T.fontM,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.1em',
            }}>
              PRE-LAUNCH
            </span>
          )}
        </Link>

        {/* HORIZON price chip - persistent across every page now that there's no
            single scrolling dashboard to anchor it to. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
          padding: isMobile ? '4px 8px' : '5px 12px',
          borderRadius: T.r.pill,
          border: `1px solid ${T.border}`,
          background: 'rgba(255,255,255,0.03)',
        }}>
          {!isMobile && (
            <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              HORIZON
            </span>
          )}
          <span className="mono" style={{ color: hasPrice ? T.positive : T.textDim, fontFamily: T.fontN, fontSize: isMobile ? T.fs.caption : T.fs.body, fontWeight: 700 }}>
            {hasPrice ? fmtUsd(horizonUsd, horizonUsd < 0.01 ? 6 : 4) : '—'}
          </span>
        </div>
        </div>

        {/* Middle column: the tab rail, centred on the screen by the grid itself. The empty div
            on mobile keeps the three-column shape while the rail is hidden. */}
        {isMobile ? <div /> : (
          <nav
            className="no-scrollbar"
            role="tablist"
            aria-label="Primary"
            // `flex: 1` makes this span everything between the brand and the wallet; centring
            // WITHIN that span is what keeps the tabs in the middle of the screen now that the bar
            // is full-bleed. Without it they sit hard against the logo, which reads as a left-
            // aligned group with a lot of empty space rather than as a centred rail.
            //
            // `justifyContent` is deliberately overridden to flex-start once the tabs overflow:
            // a centred flex row that is wider than its container puts the first item off the
            // left edge, where `overflowX: auto` cannot scroll back to it.
            style={{
              display: 'flex', gap: 2, flex: 1, overflowX: 'auto', scrollbarWidth: 'none',
              justifyContent: navOverflows ? 'flex-start' : 'center',
            }}
            ref={navRef}
          >
            {tabs.map((t) => (
              <TabButton key={t.id} active={isActive(t.id)} icon={t.icon} onClick={() => pick(t.id)}>
                {t.label}
              </TabButton>
            ))}
          </nav>
        )}

        {/* Right column: wallet + hamburger, hard against the right edge. `justifyContent:
            flex-end` is what anchors it there - the grid column is 1fr wide, so its contents would
            otherwise start at the column's left. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: isMobile ? 8 : 14 }}>
        {/* Chain selector - sits beside the wallet connector (moved off the Vaults page). Desktop
            only: on mobile the connect button + hamburger already fill the row, and the network is
            Ethereum-only for now, so nothing is lost by deferring it to a future mobile surface. */}
        {!isMobile && <ChainSelect />}
        {/* Connect button - always visible */}
        <div style={{ flexShrink: 0 }}>
          <ConnectButton
            chainStatus="none"
            accountStatus={isMobile ? 'address' : 'full'}
            showBalance={!isMobile}
          />
        </div>

        {/* Mobile hamburger - only on small viewports */}
        {isMobile && (
          <button
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            style={{
              flexShrink: 0,
              width: 38, height: 38,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: menuOpen ? T.accentDim : 'transparent',
              border: `1px solid ${T.border}`,
              borderRadius: T.r.md,
              cursor: 'pointer',
              padding: 0,
              transition: 'background 0.15s ease',
            }}
          >
            <HamburgerIcon open={menuOpen} />
          </button>
        )}
        </div>
      </div>

      {/* Mobile drawer - slides down below the header. Renders only
          when open so it doesn't capture taps otherwise. */}
      {isMobile && menuOpen && (
        <div style={{
          borderTop: `1px solid ${T.border}`,
          background: 'rgba(6, 10, 20, 0.75)',
          backdropFilter: T.glassBlur,
          WebkitBackdropFilter: T.glassBlur,
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '8px 14px 14px',
            gap: 2,
          }}>
            {tabs.map((t) => {
              const Icon = t.icon
              const active = isActive(t.id)
              return (
                <button
                  key={t.id}
                  onClick={() => pick(t.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    textAlign: 'left',
                    padding: '12px 14px',
                    borderRadius: T.r.md,
                    border: 'none',
                    background: active ? T.accentDim : 'transparent',
                    // White nav labels; active is set apart by its pill background + bold weight.
                    color: T.text,
                    fontFamily: T.fontM,
                    fontSize: T.fs.ui,
                    fontWeight: active ? 700 : 500,
                    cursor: 'pointer',
                    width: '100%',
                  }}
                >
                  {Icon && <Icon style={{ flexShrink: 0, opacity: active ? 1 : 0.75 }} />}
                  {t.label}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </header>
  )
}

function TabButton({ active, children, icon: Icon, onClick }) {
  return (
    <button
      onClick={onClick}
      role="tab"
      aria-selected={active}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '8px 8px 6px',
        border: 'none',
        background: 'transparent',
        // White nav labels; the active tab is marked by an underline (a 2px bottom border, kept
        // transparent when inactive so switching tabs never shifts the row height).
        borderBottom: `2px solid ${active ? T.text : 'transparent'}`,
        color: T.text,
        fontFamily: T.fontM,
        fontSize: T.fs.ui,
        fontWeight: active ? 700 : 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'all 0.15s ease',
      }}
    >
      {Icon && <Icon width={18} height={18} style={{ flexShrink: 0, opacity: active ? 1 : 0.75 }} />}
      {children}
    </button>
  )
}

function HamburgerIcon({ open }) {
  const common = {
    strokeWidth: 2,
    strokeLinecap: 'round',
    transition: 'all 0.2s ease',
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={open ? T.accent : T.textMuted}>
      <line x1="4"  y1={open ? 12 : 7}  x2="20" y2={open ? 12 : 7}  {...common} style={{ transform: open ? 'rotate(45deg)' : 'none', transformOrigin: '50% 50%', ...common }} />
      <line x1="4"  y1="12" x2="20" y2="12" {...common} style={{ opacity: open ? 0 : 1, ...common }} />
      <line x1="4"  y1={open ? 12 : 17} x2="20" y2={open ? 12 : 17} {...common} style={{ transform: open ? 'rotate(-45deg)' : 'none', transformOrigin: '50% 50%', ...common }} />
    </svg>
  )
}
